import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Brain, Search, RefreshCw, Tag, Clock, Filter, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

import { useChatStore } from '../../stores/useChatStore';

interface AgentMemoryPageProps {
  isOpen: boolean;
  onClose: () => void;
  fullScreen?: boolean;
}

interface MemoryEntry {
  id: string;
  ts: Date;
  type: string;
  excerpt: string;
  tags: string[];
  content: string;
  metadata: {
    source: string;
    confidence: number;
    relatedTaskId?: string;
    agent: string;
    status: 'completed' | 'failed' | 'running' | 'pending';
    progress?: number;
  };
}

const AgentMemoryPage: React.FC<AgentMemoryPageProps> = ({ isOpen, onClose, fullScreen = true }) => {
  const [memories, setMemories] = useState<MemoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedAgent, setSelectedAgent] = useState('All');
  const [selectedMemory, setSelectedMemory] = useState<MemoryEntry | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const { sessionId } = useChatStore();

  const loadMemories = useCallback(async () => {
    if (!sessionId) return;
    
    setLoading(true);
    try {
      const mockMemories: MemoryEntry[] = [
        {
          id: 'mem_001',
          ts: new Date(Date.now() - 300000),
          type: 'Security Analysis',
          excerpt: 'Queued for security vulnerability scan',
          tags: ['security', 'vulnerability', 'scan'],
          content: 'Security-Bee has been queued to perform a comprehensive security vulnerability scan of the codebase. This includes checking for known CVEs, dependency vulnerabilities, and code security patterns.',
          metadata: {
            source: 'Security-Bee',
            confidence: 95,
            relatedTaskId: 'task_security_001',
            agent: 'Security-Bee',
            status: 'pending',
            progress: 0
          }
        },
        {
          id: 'mem_002',
          ts: new Date(Date.now() - 600000),
          type: 'Performance Monitoring',
          excerpt: 'Monitoring application performance metrics',
          tags: ['performance', 'monitoring', 'metrics'],
          content: 'Monitor-Bee is actively monitoring application performance metrics including response times, memory usage, CPU utilization, and database query performance. Current metrics show stable performance within acceptable thresholds.',
          metadata: {
            source: 'Monitor-Bee',
            confidence: 76,
            relatedTaskId: 'task_monitor_001',
            agent: 'Monitor-Bee',
            status: 'running',
            progress: 76
          }
        },
        {
          id: 'mem_003',
          ts: new Date(Date.now() - 900000),
          type: 'Test Execution',
          excerpt: 'Failed to execute comprehensive test suite',
          tags: ['testing', 'failure', 'environment'],
          content: 'Test-Bee attempted to execute the comprehensive test suite but encountered failures due to missing environment variables. The following variables need to be configured: TEST_DATABASE_URL, API_KEY, and REDIS_URL.',
          metadata: {
            source: 'Test-Bee',
            confidence: 45,
            relatedTaskId: 'task_test_001',
            agent: 'Test-Bee',
            status: 'failed',
            progress: 45
          }
        },
        {
          id: 'mem_004',
          ts: new Date(Date.now() - 1200000),
          type: 'Deployment',
          excerpt: 'Successfully deployed application to staging environment',
          tags: ['deployment', 'staging', 'success'],
          content: 'Deploy-Bee successfully deployed the application to the staging environment. All health checks are passing and the application is accessible at https://staging.example.com. Database migrations completed successfully.',
          metadata: {
            source: 'Deploy-Bee',
            confidence: 88,
            relatedTaskId: 'task_deploy_001',
            agent: 'Deploy-Bee',
            status: 'completed',
            progress: 100
          }
        },
        {
          id: 'mem_005',
          ts: new Date(Date.now() - 1800000),
          type: 'Code Analysis',
          excerpt: 'Analyzed codebase and identified 3 optimization opportunities',
          tags: ['analysis', 'optimization', 'performance'],
          content: 'CodeAnalyzer-Bee performed a comprehensive analysis of the codebase and identified 3 key optimization opportunities: 1) Implement caching layer for frequently accessed data, 2) Optimize database queries in the user service, 3) Reduce bundle size by implementing code splitting.',
          metadata: {
            source: 'CodeAnalyzer-Bee',
            confidence: 92,
            relatedTaskId: 'task_analysis_001',
            agent: 'CodeAnalyzer-Bee',
            status: 'completed',
            progress: 100
          }
        }
      ];
      
      setMemories(mockMemories);
    } catch (error) {
      console.error('Failed to load memories:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen) {
      loadMemories();
    }
  }, [sessionId, isOpen, loadMemories]);

  const handleViewDetails = (memory: MemoryEntry) => {
    setSelectedMemory(memory);
    setShowDetailDrawer(true);
  };

  const getFilteredMemories = () => {
    return memories.filter(memory => {
      const matchesSearch = memory.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           memory.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           memory.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           memory.metadata.agent.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = selectedType === 'All' || memory.type === selectedType;
      const matchesAgent = selectedAgent === 'All' || memory.metadata.agent === selectedAgent;
      
      return matchesSearch && matchesType && matchesAgent;
    });
  };

  const filteredMemories = getFilteredMemories();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400 bg-green-400/10';
      case 'running':
        return 'text-blue-400 bg-blue-400/10';
      case 'failed':
        return 'text-red-400 bg-red-400/10';
      case 'pending':
        return 'text-yellow-400 bg-yellow-400/10';
      default:
        return 'text-slate-400 bg-slate-400/10';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 60) return 'bg-blue-500';
    if (progress >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (!fullScreen) return null;

  return (
    <>
      <div className="flex flex-col h-full w-full bg-slate-900">
        <div className="flex items-center justify-between p-6 border-b border-slate-700 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gradient text-drop-shadow">Agent Memory</h2>
            <p className="text-slate-400 mt-1">
              Expose the AI Agent's long-term "memories" — snippets of context, facts, or decisions it has stored to guide future actions.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 border-b border-slate-700 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Search memories by content, tags, or agent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 violett-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-slate-400" />
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="violett-input"
              >
                <option value="All">All Types</option>
                <option value="Security Analysis">Security Analysis</option>
                <option value="Performance Monitoring">Performance Monitoring</option>
                <option value="Test Execution">Test Execution</option>
                <option value="Deployment">Deployment</option>
                <option value="Code Analysis">Code Analysis</option>
              </select>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="violett-input"
              >
                <option value="All">All Agents</option>
                <option value="Security-Bee">Security-Bee</option>
                <option value="Monitor-Bee">Monitor-Bee</option>
                <option value="Test-Bee">Test-Bee</option>
                <option value="Deploy-Bee">Deploy-Bee</option>
                <option value="CodeAnalyzer-Bee">CodeAnalyzer-Bee</option>
              </select>
              <Button variant="ghost" size="icon" onClick={loadMemories} disabled={loading}>
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 p-6 overflow-auto min-h-0">
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-400">Loading memories...</span>
              </div>
            ) : filteredMemories.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-slate-400">
                <Brain className="w-12 h-12 mb-4 opacity-50" />
                <h3 className="text-lg font-medium text-slate-300 mb-2">No memories found</h3>
                <p className="text-sm">Try adjusting your search or filters</p>
              </div>
            ) : (
              filteredMemories.map((memory) => (
                <motion.div
                  key={memory.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300 cursor-pointer group"
                  onClick={() => handleViewDetails(memory)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="flex items-center space-x-2">
                          <Brain className="w-5 h-5 text-brand-accent" />
                          <span className="font-medium text-brand-text">{memory.metadata.agent}</span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(memory.metadata.status)}`}>
                          {memory.metadata.status}
                        </span>
                        {memory.metadata.progress !== undefined && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-slate-400">{memory.metadata.progress}%</span>
                            <div className="w-16 h-1 bg-slate-700 rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all duration-300 ${getProgressColor(memory.metadata.progress)}`}
                                style={{ width: `${memory.metadata.progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-gradient transition-colors">
                        {memory.type}
                      </h3>
                      
                      <p className="text-slate-300 text-sm mb-3 line-clamp-2">
                        {memory.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-xs text-slate-400">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{memory.ts.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span>Confidence: {memory.metadata.confidence}%</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <div className="flex flex-wrap gap-1">
                            {memory.tags.slice(0, 3).map((tag) => (
                              <span key={tag} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-300">
                                <Tag className="w-3 h-3 mr-1" />
                                {tag}
                              </span>
                            ))}
                            {memory.tags.length > 3 && (
                              <span className="text-xs text-slate-400">+{memory.tags.length - 3} more</span>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-brand-accent transition-colors" />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showDetailDrawer && selectedMemory && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={() => setShowDetailDrawer(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, x: 600 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 600 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-[600px] bg-slate-900 border-l border-slate-700 z-50 flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-slate-700">
                <div>
                  <h3 className="text-xl font-bold text-white">Memory Details</h3>
                  <p className="text-sm text-slate-400">{selectedMemory.metadata.agent}</p>
                </div>
                <button
                  onClick={() => setShowDetailDrawer(false)}
                  className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="flex-1 p-6 overflow-auto">
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">{selectedMemory.type}</h4>
                    <div className="flex items-center space-x-4 text-sm text-slate-400 mb-4">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{selectedMemory.ts.toLocaleString()}</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedMemory.metadata.status)}`}>
                        {selectedMemory.metadata.status}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium text-slate-300 mb-2">Content</h5>
                    <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
                      <p className="text-slate-300 text-sm leading-relaxed">{selectedMemory.content}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium text-slate-300 mb-2">Tags</h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedMemory.tags.map((tag) => (
                        <span key={tag} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-slate-700 text-slate-300">
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium text-slate-300 mb-2">Metadata</h5>
                    <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Source:</span>
                        <span className="text-slate-300">{selectedMemory.metadata.source}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Confidence:</span>
                        <span className="text-slate-300">{selectedMemory.metadata.confidence}%</span>
                      </div>
                      {selectedMemory.metadata.relatedTaskId && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Related Task:</span>
                          <span className="text-slate-300 font-mono text-xs">{selectedMemory.metadata.relatedTaskId}</span>
                        </div>
                      )}
                      {selectedMemory.metadata.progress !== undefined && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Progress:</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-slate-300">{selectedMemory.metadata.progress}%</span>
                            <div className="w-20 h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all duration-300 ${getProgressColor(selectedMemory.metadata.progress)}`}
                                style={{ width: `${selectedMemory.metadata.progress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default AgentMemoryPage;
